import React, { useState, useEffect } from "react";
import { Plus, Search, Edit, Trash2, Upload, Zap, DollarSign, Scale, Package, Clock, TrendingDown, Download, History, AlertTriangle } from "lucide-react";
import { SearchableSelect } from "../components/ui/searchable-select";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN, categoryLabels } from "../utils/format";
import ImportModal from "../components/ImportModal";

const CATEGORIES = ["producto_final", "materia_prima", "insumo_directo", "insumo_indirecto", "material_empaque"];
const UNITS = ["kg", "g", "pza", "lt", "ml", "galon", "bolsa", "caja", "rollo"];

function PriceTiers({ tiers, onChange, saleUnit }) {
  const u = saleUnit || "kg";
  const add = () => onChange([...tiers, { min_qty: 0, max_qty: null, price: 0 }]);
  const remove = (i) => onChange(tiers.filter((_, idx) => idx !== i));
  const update = (i, field, value) => {
    const updated = [...tiers];
    updated[i] = { ...updated[i], [field]: value === "" ? null : Number(value) };
    onChange(updated);
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-600">Precios por volumen</span>
        <button type="button" onClick={add} className="text-xs text-brand-600 hover:text-brand-700 font-medium">+ Agregar rango</button>
      </div>
      {tiers.map((t, i) => (
        <div key={i} className="flex items-center gap-2 mb-2">
          <input type="number" placeholder={`Desde ${u}`} value={t.min_qty || 0} onChange={(e) => update(i, "min_qty", e.target.value)}
            className="w-24 border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
          <span className="text-xs text-slate-400">a</span>
          <input type="number" placeholder={`Hasta ${u}`} value={t.max_qty ?? ""} onChange={(e) => update(i, "max_qty", e.target.value)}
            className="w-24 border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
          <span className="text-xs text-slate-400">$/{u}:</span>
          <input type="number" placeholder="0.00" value={t.price || 0} onChange={(e) => update(i, "price", e.target.value)}
            className="w-28 border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
          <button type="button" onClick={() => remove(i)} className="text-red-400 hover:text-red-600 text-xs font-bold">x</button>
        </div>
      ))}
      {tiers.length === 0 && <p className="text-xs text-slate-400 italic">Sin rangos de precio por volumen</p>}
    </div>
  );
}

function CostTable({ items, onChange, label, color, costUnit, hint, placeholderExamples }) {
  const u = costUnit || "kg";
  const add = () => onChange([...items, { name: "", cost_per_kg: 0 }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, field, value) => {
    const updated = [...items];
    updated[i] = { ...updated[i], [field]: field === "cost_per_kg" ? Number(value) || 0 : value };
    onChange(updated);
  };
  const total = items.reduce((s, c) => s + (c.cost_per_kg || 0), 0);
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className={`text-xs font-bold uppercase tracking-wide ${color}`}>{label}</span>
        <button type="button" onClick={add} className="text-xs text-brand-600 hover:text-brand-700 font-medium">+ Agregar</button>
      </div>
      {hint && <p className="text-[10px] text-slate-400 mb-2">{hint}</p>}
      {items.map((c, i) => (
        <div key={i} className="flex items-center gap-2 mb-1.5">
          <input value={c.name} onChange={(e) => update(i, "name", e.target.value)}
            placeholder={placeholderExamples?.[i] || "Concepto..."}
            className="flex-1 border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-400">$</span>
            <input type="number" value={c.cost_per_kg || ""} onChange={(e) => update(i, "cost_per_kg", e.target.value)}
              placeholder="0.00" min="0" step="0.01"
              className="w-24 border border-slate-200 rounded px-2 py-1.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-brand-500" />
            <span className="text-xs text-slate-400">/{u}</span>
          </div>
          <button type="button" onClick={() => remove(i)} className="text-red-400 hover:text-red-600 text-xs px-1 font-bold">x</button>
        </div>
      ))}
      {items.length > 0 && (
        <div className={`text-right text-xs font-semibold mt-1 ${color}`}>
          Subtotal: {formatMXN(total)}/{u}
        </div>
      )}
    </div>
  );
}

function CostSummary({ form }) {
  const costUnit = form.cost_unit || "kg";
  const direct = (form.direct_costs || []).reduce((s, c) => s + (c.cost_per_kg || 0), 0);
  const indirect = (form.indirect_costs || []).reduce((s, c) => s + (c.cost_per_kg || 0), 0);
  const total = direct + indirect;
  const ppk = form.pieces_per_kg || 0;

  let costPerKg, costPerPiece;
  if (costUnit === "pza") {
    costPerPiece = total;
    costPerKg = ppk > 0 ? total * ppk : 0;
  } else {
    costPerKg = total;
    costPerPiece = ppk > 0 ? total / ppk : 0;
  }

  const yld = form.yield_pct || 0;
  const raw = form.min_batch_raw_kg || 0;
  const yieldType = form.yield_type || "peso";
  let finished, batchPieces;
  if (yieldType === "conteo") {
    finished = raw;
    batchPieces = raw * ppk;
  } else {
    finished = yld > 0 ? raw * yld / 100 : 0;
    batchPieces = finished * ppk;
  }

  if (total === 0) return null;

  const saleUnit = form.unit || "kg";
  const sellUnitCost = saleUnit === "pza" ? costPerPiece : costPerKg;

  return (
    <div className="bg-slate-50 rounded-lg p-3 space-y-1.5" data-testid="cost-summary">
      <div className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">Resumen de Costos</div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
        <span className="text-slate-500">Directos/{costUnit}:</span>
        <span className="font-semibold text-right">{formatMXN(direct)}</span>
        <span className="text-slate-500">Indirectos/{costUnit}:</span>
        <span className="font-semibold text-right">{formatMXN(indirect)}</span>
        <span className="text-slate-700 font-bold">Total/{costUnit}:</span>
        <span className="font-bold text-right text-brand-700">{formatMXN(total)}</span>
        {costUnit === "kg" && ppk > 0 && <>
          <span className="text-slate-500">Costo/pieza:</span>
          <span className="font-semibold text-right">{formatMXN(costPerPiece)}</span>
        </>}
        {costUnit === "pza" && ppk > 0 && <>
          <span className="text-slate-500">Costo/kg (ref.):</span>
          <span className="font-semibold text-right">{formatMXN(costPerKg)}</span>
        </>}
        {raw > 0 && <>
          <span className="text-slate-500 col-span-2 mt-1 pt-1 border-t border-slate-200">
            Lote min: {raw} {form.min_batch_unit || form.purchase_unit || "kg"}
            {yieldType === "peso" && yld > 0 && <> &rarr; {finished.toFixed(1)}kg terminado</>}
            {yieldType === "conteo" && <> &rarr; 100% conteo</>}
            {ppk > 0 && <> &rarr; ~{Math.round(batchPieces)} pzas</>}
          </span>
        </>}
      </div>
      {form.price > 0 && sellUnitCost > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-200 text-xs">
          <span className="text-slate-500">Margen bruto ({saleUnit}): </span>
          <span className={`font-bold ${(form.price - sellUnitCost) > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatMXN(form.price - sellUnitCost)} ({((form.price - sellUnitCost) / form.price * 100).toFixed(1)}%)
          </span>
        </div>
      )}
    </div>
  );
}

function ProductForm({ product, products, onClose, onSave }) {
  const isInsumo = (cat) => ["insumo_directo", "insumo_indirecto", "materia_prima", "material_empaque"].includes(cat);

  const [form, setForm] = useState({
    name: "", sku: "", category: "producto_final", unit: "kg",
    purchase_unit: "kg", cost_unit: "kg", yield_type: "peso",
    purchase_cost: 0, cost: 0, price: 0, can_produce: false, ingredients: [],
    price_tiers: [], description: "", active: true, min_stock: 0,
    direct_costs: [], indirect_costs: [],
    yield_pct: 0, min_batch_raw_kg: 0, min_batch_unit: "kg", pieces_per_kg: 0,
    typical_oven_hours: 0, typical_batch_costs: [], output_products: [],
    purchase_presentation: "", units_per_presentation: 0, presentations_per_batch: 0,
    cutting_waste_pct: 0, oven_waste_pct: 0,
    estimated_gas_per_kg: 0, estimated_electricity_per_kg: 0,
    estimated_labor_per_kg: 0, estimated_raw_material_per_kg: 0,
    estimated_extras_per_kg: 0, estimated_total_cost_per_kg: 0,
    cost_alert_threshold_pct: 15,
  });
  const [saving, setSaving] = useState(false);
  const [openSections, setOpenSections] = useState({
    produccion: true, receta: false, costos: false, merma: false, salida: false, precios: false
  });

  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    if (product) {
      setForm((prev) => ({
        ...prev, ...product,
        purchase_unit: product.purchase_unit || product.unit || "kg",
        cost_unit: product.cost_unit || "kg",
        yield_type: product.yield_type || "peso",
        min_batch_unit: product.min_batch_unit || product.purchase_unit || "kg",
      }));
      // Auto-open sections that have data
      setOpenSections({
        produccion: !!product.can_produce,
        receta: (product.ingredients?.length > 0),
        costos_est: !!(product.estimated_gas_per_kg || product.estimated_electricity_per_kg || product.estimated_raw_material_per_kg || product.estimated_labor_per_kg || product.estimated_extras_per_kg),
        costos: (product.typical_batch_costs?.length > 0),
        merma: !!(product.purchase_presentation || product.cutting_waste_pct || product.oven_waste_pct),
        salida: (product.output_products?.length > 0),
        precios: (product.price_tiers?.length > 0),
      });
    }
  }, [product]);

  const addIngredient = () => {
    setForm((f) => ({
      ...f, ingredients: [...(f.ingredients || []),
        { product_id: "", product_name: "", quantity: 0, unit: "kg" }]
    }));
  };

  const updateIngredient = (i, field, value) => {
    setForm((prev) => {
      const updated = [...(prev.ingredients || [])];
      if (field === "product_id") {
        const prod = products.find((p) => p.id === value);
        updated[i] = { ...updated[i], product_id: value, product_name: prod?.name || "", unit: prod?.unit || "kg" };
      } else {
        updated[i] = { ...updated[i], [field]: field === "quantity" ? Number(value) : value };
      }
      return { ...prev, ingredients: updated };
    });
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("El nombre es requerido"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (isInsumo(form.category)) {
        payload.cost = payload.price;
      }
      if (product) {
        await api.put(`/products/${product.id}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/products", payload);
        toast.success("Producto creado");
      }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const f = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const isFinal = form.category === "producto_final";

  const Section = ({ id, title, icon, children, color = "slate", badge }) => (
    <div className={`border border-${color}-200 rounded-lg overflow-hidden`}>
      <button type="button" onClick={() => toggleSection(id)}
        className={`w-full flex items-center justify-between px-4 py-2.5 text-left ${openSections[id] ? `bg-${color}-50` : "bg-white hover:bg-slate-50"} transition-colors`}>
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-medium text-slate-700">{title}</span>
          {badge && <span className="text-[10px] bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded-full font-medium">{badge}</span>}
        </div>
        <span className="text-slate-400 text-xs">{openSections[id] ? "▲" : "▼"}</span>
      </button>
      {openSections[id] && <div className="p-4 space-y-3 border-t border-slate-100">{children}</div>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="product-form-overlay">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-50 bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 mx-4" data-testid="product-form">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{product ? "Editar producto" : "Nuevo producto"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-700">
            <span className="text-xl leading-none">&times;</span>
          </button>
        </div>
        <div className="space-y-4 py-2">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs text-slate-500 mb-1">Nombre *</label>
              <input value={form.name} onChange={(e) => f("name", e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                placeholder="Nombre del producto" data-testid="product-name-input" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">SKU/Codigo</label>
              <input value={form.sku} onChange={(e) => f("sku", e.target.value)}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Categoria</label>
              <select value={form.category} onChange={(e) => f("category", e.target.value)}
                data-testid="product-category-select"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand-500">
                {CATEGORIES.map((c) => <option key={c} value={c}>{categoryLabels[c]}</option>)}
              </select>
            </div>
          </div>

          {/* Units & Pricing */}
          <div className="border border-slate-200 rounded-lg p-4" data-testid="units-section">
            <div className="flex items-center gap-1.5 mb-3">
              <Scale size={14} className="text-slate-600" />
              <span className="text-sm font-medium text-slate-700">Unidades y {isFinal ? "Precio" : "Costo"}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {isFinal ? (
                <>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Unidad de compra</label>
                    <select value={form.purchase_unit} onChange={(e) => f("purchase_unit", e.target.value)}
                      data-testid="purchase-unit-select"
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand-500">
                      {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <span className="text-[10px] text-slate-400">Como compras la materia prima</span>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Unidad de venta</label>
                    <select value={form.unit} onChange={(e) => f("unit", e.target.value)}
                      data-testid="sale-unit-select"
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand-500">
                      {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <span className="text-[10px] text-slate-400">Como lo vendes al cliente</span>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Precio de venta/{form.unit}</label>
                    <input type="number" value={form.price} onChange={(e) => f("price", Number(e.target.value))}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                      min="0" step="0.01" data-testid="product-price-input" />
                    <span className="text-[10px] text-slate-400">Ref. Las listas de precio definen precios por volumen/cliente</span>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Stock minimo ({form.unit})</label>
                    <input type="number" value={form.min_stock} onChange={(e) => f("min_stock", Number(e.target.value))}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" min="0" step="0.01" />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Unidad</label>
                    <select value={form.unit} onChange={(e) => { f("unit", e.target.value); f("purchase_unit", e.target.value); }}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-brand-500">
                      {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Costo por {form.unit}</label>
                    <input type="number" value={form.price} onChange={(e) => f("price", Number(e.target.value))}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                      min="0" step="0.01" data-testid="product-cost-input" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Stock minimo</label>
                    <input type="number" value={form.min_stock} onChange={(e) => f("min_stock", Number(e.target.value))}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" min="0" step="0.01" />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Descripcion</label>
            <textarea value={form.description} onChange={(e) => f("description", e.target.value)}
              rows={2} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
          </div>

          {/* Production Section */}
          {isFinal && (
            <Section id="produccion" title="Produccion" icon={<Package size={14} className="text-slate-600" />}>
              <div className="flex items-center gap-3 mb-2">
                <input type="checkbox" id="can_produce" checked={form.can_produce}
                  onChange={(e) => f("can_produce", e.target.checked)}
                  className="w-4 h-4 accent-brand-600" />
                <label htmlFor="can_produce" className="text-sm text-slate-700">Se puede fabricar (lo deshidratas tu)</label>
              </div>
              {form.can_produce && (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Rendimiento (%)</label>
                      {form.yield_type === "peso" ? (
                        <input type="number" value={form.yield_pct || ""} onChange={(e) => f("yield_pct", Number(e.target.value))}
                          placeholder="Ej: 31.5" min="0" max="100" step="0.1"
                          className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
                      ) : (
                        <span className="text-xs text-emerald-600 font-medium block mt-1.5">100% (conteo)</span>
                      )}
                      <span className="text-[10px] text-slate-400">% crudo que sale terminado</span>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Tipo rendimiento</label>
                      <select value={form.yield_type} onChange={(e) => { f("yield_type", e.target.value); if (e.target.value === "conteo") f("yield_pct", 100); }}
                        className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs bg-white" data-testid="yield-type-select">
                        <option value="peso">Por peso (kg)</option>
                        <option value="conteo">Por conteo (pzas)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Pzas/kg (ref.)</label>
                      <input type="number" value={form.pieces_per_kg || ""} onChange={(e) => f("pieces_per_kg", Number(e.target.value))}
                        placeholder="Ej: 6.77" min="0" step="0.01"
                        className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Lote minimo materia prima</label>
                    <div className="flex items-center gap-2">
                      <input type="number" value={form.min_batch_raw_kg || ""} onChange={(e) => f("min_batch_raw_kg", Number(e.target.value))}
                        placeholder="Ej: 90" min="0" step="0.1"
                        className="w-32 border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
                        data-testid="min-batch-input" />
                      <select value={form.min_batch_unit || "kg"} onChange={(e) => f("min_batch_unit", e.target.value)}
                        className="w-20 border border-slate-200 rounded px-2 py-1.5 text-xs bg-white" data-testid="min-batch-unit-select">
                        <option value="kg">kg</option><option value="pza">pza</option>
                      </select>
                    </div>
                  </div>
                  {/* Quick production summary */}
                  {form.min_batch_raw_kg > 0 && form.yield_pct > 0 && (
                    <div className="bg-slate-50 rounded p-2 text-xs text-slate-600">
                      <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-medium">{form.min_batch_raw_kg} kg crudo</span>
                      <span className="mx-2">&rarr;</span>
                      <span className="text-slate-700 font-medium">{(form.min_batch_raw_kg * form.yield_pct / 100).toFixed(1)} kg terminado ({form.yield_pct}%)</span>
                    </div>
                  )}
                </div>
              )}
            </Section>
          )}

          {/* Recipe / Ingredients */}
          {isFinal && form.can_produce && (
            <Section id="receta" title="Receta / Materias Primas" icon={<Scale size={14} className="text-amber-600" />}
              badge={`${(form.ingredients || []).length}`}>
              <p className="text-[10px] text-slate-400 mb-2">Cantidad de cada insumo por 1 {form.unit || "kg"} de producto terminado.</p>
              {(form.ingredients || []).map((ing, i) => {
                const ingProd = products.find(p => p.id === ing.product_id);
                const ingStock = ingProd ? ingProd.stock : null;
                return (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <select value={ing.product_id || ""} onChange={(e) => updateIngredient(i, "product_id", e.target.value)}
                    className="flex-1 h-8 border border-slate-200 rounded-md px-2 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 bg-white"
                    data-testid={`ingredient-select-${i}`}>
                    <option value="">Seleccionar insumo...</option>
                    {products.filter(p => p.id !== form.id).map((p) => (
                      <option key={p.id} value={p.id}>{p.name} {p.category ? `[${categoryLabels[p.category] || p.category}]` : ""}</option>
                    ))}
                  </select>
                  <input type="number" placeholder={`por ${form.unit || "kg"}`} value={ing.quantity || 0}
                    onChange={(e) => updateIngredient(i, "quantity", e.target.value)}
                    className="w-20 border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
                    title={`Kg de este insumo por cada 1 ${form.unit} de ${form.name || "producto terminado"}`} />
                  <select value={ing.unit} onChange={(e) => updateIngredient(i, "unit", e.target.value)}
                    className="w-16 h-8 border border-slate-200 rounded-md px-1 text-xs bg-white">
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                  {ingStock !== null && (
                    <span className={`text-[10px] font-medium whitespace-nowrap ${ingStock > 0 ? "text-emerald-600" : "text-amber-500"}`}>
                      {ingStock > 0 ? `${ingStock}` : "0"}
                    </span>
                  )}
                  <button type="button" onClick={() => setForm((prev) => ({
                    ...prev, ingredients: prev.ingredients.filter((_, idx) => idx !== i)
                  }))} className="text-red-400 hover:text-red-600 text-xs font-bold">x</button>
                </div>
                );
              })}
              <button type="button" onClick={addIngredient} className="text-xs text-brand-600 hover:text-brand-700 font-medium">+ Agregar ingrediente</button>
              {(form.ingredients || []).length === 0 && <p className="text-xs text-slate-400 italic mt-1">Agrega los insumos (ej: pulmon crudo, higado crudo).</p>}
            </Section>
          )}

          {/* Supplier Packaging & Waste */}
          {isFinal && form.can_produce && (
            <Section id="merma" title="Empaque proveedor y mermas" icon={<TrendingDown size={14} className="text-amber-600" />}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-slate-500 mb-0.5">Presentacion (ej: caja, costal)</label>
                  <input type="text" value={form.purchase_presentation || ""} onChange={(e) => f("purchase_presentation", e.target.value)}
                    placeholder="Ej: caja" data-testid="purchase-presentation-input"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-500 mb-0.5">Kg por {form.purchase_presentation || "presentacion"}</label>
                  <input type="number" value={form.units_per_presentation || ""} onChange={(e) => f("units_per_presentation", Number(e.target.value))}
                    placeholder="Ej: 20" min="0" step="0.1" data-testid="units-per-presentation-input"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-500 mb-0.5">% Merma al cortar</label>
                  <input type="number" value={form.cutting_waste_pct || ""} onChange={(e) => f("cutting_waste_pct", Number(e.target.value))}
                    placeholder="Ej: 10" min="0" max="100" step="0.5" data-testid="cutting-waste-input"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-500 mb-0.5">% Merma horno</label>
                  <input type="number" value={form.oven_waste_pct || ""} onChange={(e) => f("oven_waste_pct", Number(e.target.value))}
                    placeholder="Ej: 2" min="0" max="100" step="0.5" data-testid="oven-waste-input"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
                </div>
              </div>
            </Section>
          )}

          {/* Estimated Costs (user-defined targets) */}
          {isFinal && (
            <Section id="costos_est" title="Costos Estimados por kg" icon={<DollarSign size={14} className="text-red-600" />}
              badge={form.estimated_total_cost_per_kg > 0 ? `$${form.estimated_total_cost_per_kg}` : null}>
              <p className="text-[10px] text-slate-400 mb-2">Tu costo meta. Al completar un lote, se compara con el costo real y recibes alerta si la diferencia es mayor al umbral.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] text-slate-500 mb-0.5">Gas / kg</label>
                  <input type="number" value={form.estimated_gas_per_kg || ""} onChange={e => {
                    const v = Number(e.target.value) || 0;
                    setForm(prev => {
                      const newForm = { ...prev, estimated_gas_per_kg: v };
                      newForm.estimated_total_cost_per_kg = (v + (prev.estimated_electricity_per_kg || 0) + (prev.estimated_labor_per_kg || 0) + (prev.estimated_raw_material_per_kg || 0) + (prev.estimated_extras_per_kg || 0));
                      return newForm;
                    });
                  }} placeholder="$0.00" min="0" step="0.01" data-testid="est-gas"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-500 mb-0.5">Luz / kg</label>
                  <input type="number" value={form.estimated_electricity_per_kg || ""} onChange={e => {
                    const v = Number(e.target.value) || 0;
                    setForm(prev => {
                      const newForm = { ...prev, estimated_electricity_per_kg: v };
                      newForm.estimated_total_cost_per_kg = ((prev.estimated_gas_per_kg || 0) + v + (prev.estimated_labor_per_kg || 0) + (prev.estimated_raw_material_per_kg || 0) + (prev.estimated_extras_per_kg || 0));
                      return newForm;
                    });
                  }} placeholder="$0.00" min="0" step="0.01" data-testid="est-elec"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-500 mb-0.5">Mano de obra / kg</label>
                  <input type="number" value={form.estimated_labor_per_kg || ""} onChange={e => {
                    const v = Number(e.target.value) || 0;
                    setForm(prev => {
                      const newForm = { ...prev, estimated_labor_per_kg: v };
                      newForm.estimated_total_cost_per_kg = ((prev.estimated_gas_per_kg || 0) + (prev.estimated_electricity_per_kg || 0) + v + (prev.estimated_raw_material_per_kg || 0) + (prev.estimated_extras_per_kg || 0));
                      return newForm;
                    });
                  }} placeholder="$0.00" min="0" step="0.01" data-testid="est-labor"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-500 mb-0.5">Materia prima / kg</label>
                  <input type="number" value={form.estimated_raw_material_per_kg || ""} onChange={e => {
                    const v = Number(e.target.value) || 0;
                    setForm(prev => {
                      const newForm = { ...prev, estimated_raw_material_per_kg: v };
                      newForm.estimated_total_cost_per_kg = ((prev.estimated_gas_per_kg || 0) + (prev.estimated_electricity_per_kg || 0) + (prev.estimated_labor_per_kg || 0) + v + (prev.estimated_extras_per_kg || 0));
                      return newForm;
                    });
                  }} placeholder="$0.00" min="0" step="0.01" data-testid="est-raw"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-500 mb-0.5">Extras / kg (sierra, etc)</label>
                  <input type="number" value={form.estimated_extras_per_kg || ""} onChange={e => {
                    const v = Number(e.target.value) || 0;
                    setForm(prev => {
                      const newForm = { ...prev, estimated_extras_per_kg: v };
                      newForm.estimated_total_cost_per_kg = ((prev.estimated_gas_per_kg || 0) + (prev.estimated_electricity_per_kg || 0) + (prev.estimated_labor_per_kg || 0) + (prev.estimated_raw_material_per_kg || 0) + v);
                      return newForm;
                    });
                  }} placeholder="$0.00" min="0" step="0.01" data-testid="est-extras"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500" />
                </div>
                <div>
                  <label className="block text-[10px] text-slate-500 mb-0.5">Umbral alerta (%)</label>
                  <select value={form.cost_alert_threshold_pct} onChange={e => f("cost_alert_threshold_pct", Number(e.target.value))}
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-xs bg-white" data-testid="cost-alert-threshold">
                    {[5, 10, 15, 20, 25, 30].map(v => <option key={v} value={v}>{v}%</option>)}
                  </select>
                </div>
              </div>
              {form.estimated_total_cost_per_kg > 0 && (
                <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-slate-500">Costo estimado total:</span>
                    <span className="text-lg font-bold text-slate-900 ml-2">{formatMXN(form.estimated_total_cost_per_kg)} / kg</span>
                  </div>
                  {form.price > 0 && (
                    <div className="text-right">
                      <span className="text-xs text-slate-500">Margen estimado:</span>
                      <span className={`text-sm font-bold ml-1 ${form.price - form.estimated_total_cost_per_kg > 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {formatMXN(form.price - form.estimated_total_cost_per_kg)} ({((form.price - form.estimated_total_cost_per_kg) / form.price * 100).toFixed(1)}%)
                      </span>
                    </div>
                  )}
                </div>
              )}
            </Section>
          )}

          {/* Typical batch costs */}
          {isFinal && form.can_produce && (
            <Section id="costos" title="Costos tipicos por lote" icon={<Clock size={14} className="text-blue-600" />}
              badge={`${(form.typical_batch_costs || []).length}`}>
              <p className="text-[10px] text-slate-400 mb-2">Se auto-rellenan al crear un lote. Al completar, ajustas los costos reales.</p>
              <div className="mb-2">
                <label className="block text-xs text-slate-500 mb-1">Horas tipicas en horno</label>
                <input type="number" value={form.typical_oven_hours || ""} onChange={(e) => f("typical_oven_hours", Number(e.target.value))}
                  placeholder="Ej: 28" min="0" step="0.5"
                  className="w-32 border border-slate-200 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
                  data-testid="typical-oven-hours" />
              </div>
              {(form.typical_batch_costs || []).map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-1 mb-1.5 items-center" data-testid={`typical-cost-${i}`}>
                  <input value={c.name} onChange={(e) => {
                    const upd = [...(form.typical_batch_costs || [])];
                    upd[i] = { ...upd[i], name: e.target.value };
                    f("typical_batch_costs", upd);
                  }} placeholder={["Gas", "Electricidad", "Mano de obra"][i] || "Concepto"}
                    className="col-span-3 border border-slate-200 rounded px-2 py-1.5 text-xs" />
                  <input type="number" value={c.hours || ""} onChange={(e) => {
                    const upd = [...(form.typical_batch_costs || [])];
                    const h = Number(e.target.value) || 0;
                    upd[i] = { ...upd[i], hours: h, total: h * (upd[i].cost_per_hour || 0) };
                    f("typical_batch_costs", upd);
                  }} placeholder="Hrs" min="0" step="0.5"
                    className="col-span-2 border border-slate-200 rounded px-2 py-1.5 text-xs text-center" />
                  <input type="number" value={c.cost_per_hour || ""} onChange={(e) => {
                    const upd = [...(form.typical_batch_costs || [])];
                    const r = Number(e.target.value) || 0;
                    upd[i] = { ...upd[i], cost_per_hour: r, total: (upd[i].hours || 0) * r };
                    f("typical_batch_costs", upd);
                  }} placeholder="$/hr" min="0" step="0.01"
                    className="col-span-2 border border-slate-200 rounded px-2 py-1.5 text-xs text-center" />
                  <div className="col-span-3 text-xs text-right font-medium text-slate-700">${(c.total || 0).toFixed(2)}</div>
                  <div className="col-span-2 flex justify-end">
                    <button type="button" onClick={() => f("typical_batch_costs", (form.typical_batch_costs || []).filter((_, idx) => idx !== i))}
                      className="text-red-400 hover:text-red-600 text-xs font-bold px-1">x</button>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => f("typical_batch_costs", [...(form.typical_batch_costs || []), { name: "", hours: form.typical_oven_hours || 0, cost_per_hour: 0, total: 0 }])}
                className="text-xs text-brand-600 hover:text-brand-700 font-medium" data-testid="add-typical-cost">+ Agregar costo</button>
            </Section>
          )}

          {/* Output products */}
          {isFinal && form.can_produce && (
            <Section id="salida" title="Productos de salida" icon={<Package size={14} className="text-green-600" />}
              badge={`${(form.output_products || []).length}`}>
              <p className="text-[10px] text-slate-400 mb-2">Si de este producto salen varios (ej: bites, bastones), agregalos aqui.</p>
              {(form.output_products || []).map((op, i) => (
                <div key={i} className="flex items-center gap-2 mb-1.5" data-testid={`output-product-${i}`}>
                  <SearchableSelect
                    options={products.filter(p => p.category === "producto_final" && p.id !== product?.id).map(p => ({ value: p.id, label: `${p.name} (${p.unit})` }))}
                    value={op.product_id} onSelect={(val) => {
                      const prod = products.find(p => p.id === val);
                      const upd = [...(form.output_products || [])];
                      upd[i] = { product_id: val, product_name: prod?.name || "" };
                      f("output_products", upd);
                    }}
                    placeholder="Seleccionar producto de salida..."
                    data-testid={`output-product-select-${i}`}
                  />
                  <button type="button" onClick={() => f("output_products", (form.output_products || []).filter((_, idx) => idx !== i))}
                    className="text-red-400 hover:text-red-600 text-xs font-bold px-1">x</button>
                </div>
              ))}
              <button type="button" onClick={() => f("output_products", [...(form.output_products || []), { product_id: "", product_name: "" }])}
                className="text-xs text-brand-600 hover:text-brand-700 font-medium" data-testid="add-output-product">+ Agregar</button>
            </Section>
          )}

          {/* Resale Section */}
          {isFinal && !form.can_produce && (
            <div className="border border-amber-200 bg-amber-50/30 rounded-lg p-4" data-testid="resale-section">
              <div className="flex items-center gap-1.5 mb-3">
                <DollarSign size={14} className="text-amber-600" />
                <span className="text-sm font-medium text-slate-700">Compra y Reventa</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Costo de compra por {form.purchase_unit}</label>
                  <input type="number" value={form.purchase_cost || ""} onChange={(e) => f("purchase_cost", Number(e.target.value))}
                    className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                    min="0" step="0.01" placeholder="Ej: 50.00" data-testid="purchase-cost-input" />
                </div>
                {form.purchase_unit !== form.unit && (
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Pzas por {form.purchase_unit}</label>
                    <input type="number" value={form.pieces_per_kg || ""} onChange={(e) => f("pieces_per_kg", Number(e.target.value))}
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
                      min="0" step="0.01" placeholder="Ej: 10" data-testid="resale-ppk-input" />
                  </div>
                )}
              </div>
              {form.purchase_cost > 0 && form.price > 0 && (() => {
                const pc = form.purchase_cost;
                const ppk = form.pieces_per_kg || 0;
                const sameUnit = form.purchase_unit === form.unit;
                let costPerSaleUnit = sameUnit ? pc : (ppk > 0 ? pc / ppk : 0);
                const margin = costPerSaleUnit > 0 ? form.price - costPerSaleUnit : 0;
                const marginPct = form.price > 0 && margin !== 0 ? (margin / form.price * 100) : 0;
                return (
                  <div className="mt-3 bg-white rounded-lg p-3 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Margen bruto:</span>
                      <span className={`font-bold ${margin > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatMXN(margin)} ({marginPct.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Price tiers */}
          {isFinal && (
            <Section id="precios" title="Precios por volumen" icon={<DollarSign size={14} className="text-slate-600" />}
              badge={`${(form.price_tiers || []).length}`}>
              <PriceTiers tiers={form.price_tiers || []} onChange={(tiers) => f("price_tiers", tiers)} saleUnit={form.unit} />
            </Section>
          )}

          <div className="flex items-center gap-3">
            <input type="checkbox" id="active" checked={form.active} onChange={(e) => f("active", e.target.checked)}
              className="w-4 h-4 accent-brand-600" />
            <label htmlFor="active" className="text-sm text-slate-700">Producto activo</label>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancelar</button>
          <button onClick={handleSave} disabled={saving} data-testid="save-product-btn"
            className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Productos() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("todos");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [autoCreating, setAutoCreating] = useState(false);
  const [showCostHistory, setShowCostHistory] = useState(null);
  const [costHistory, setCostHistory] = useState(null);
  const [excelUploading, setExcelUploading] = useState(false);
  const [alerts, setAlerts] = useState([]);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/products"); setProducts(r.data); }
    catch { toast.error("Error al cargar productos"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); loadAlerts(); }, []);

  const loadAlerts = async () => {
    try {
      const r = await api.get("/alerts");
      setAlerts((r.data || []).filter(a => a.type === "cost_variance" && !a.read));
    } catch {}
  };

  const openCostHistory = async (product) => {
    setShowCostHistory(product);
    try {
      const r = await api.get(`/products/${product.id}/cost-history`);
      setCostHistory(r.data);
    } catch { setCostHistory(null); }
  };

  const handleExcelExport = () => {
    window.open(`${api.defaults.baseURL}/products/export/excel`, "_blank");
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExcelUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await api.post("/products/import/excel", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const { updated, created, errors } = r.data;
      toast.success(`Excel procesado: ${updated} actualizados, ${created} creados${errors?.length ? `, ${errors.length} errores` : ""}`);
      if (errors?.length) errors.forEach(err => toast.error(err));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al procesar Excel"); }
    finally { setExcelUploading(false); e.target.value = ""; }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este producto?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Producto eliminado");
    load();
  };

  const handleAutoCreate = async () => {
    setAutoCreating(true);
    try {
      const r = await api.post("/products/auto-create-from-sales");
      if (r.data.created > 0) {
        toast.success(`${r.data.created} productos creados desde ventas importadas`);
        load();
      } else {
        toast.info("Todos los productos de ventas ya estan registrados");
      }
    } catch { toast.error("Error al crear productos"); }
    finally { setAutoCreating(false); }
  };

  const filtered = products.filter((p) => {
    const matchCat = catFilter === "todos" || p.category === catFilter;
    const matchSearch = !search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.sku?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const isInsumo = (cat) => ["insumo_directo", "insumo_indirecto", "materia_prima", "material_empaque"].includes(cat);

  return (
    <div className="space-y-4" data-testid="productos-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Productos</h1>
          <p className="text-slate-500 text-sm mt-0.5">{products.length} productos registrados</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={handleExcelExport} data-testid="export-excel-btn"
            className="flex items-center gap-1.5 border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors">
            <Download size={15} /> <span className="hidden sm:inline">Descargar Excel</span>
          </button>
          <label className={`flex items-center gap-1.5 border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${excelUploading ? "opacity-60 pointer-events-none" : ""}`}
            data-testid="upload-excel-btn">
            <Upload size={15} /> <span className="hidden sm:inline">{excelUploading ? "Procesando..." : "Subir Excel"}</span>
            <input type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} className="hidden" />
          </label>
          <button onClick={handleAutoCreate} disabled={autoCreating} data-testid="auto-create-btn"
            className="flex items-center gap-1.5 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60">
            <Zap size={15} /> <span className="hidden sm:inline">{autoCreating ? "Creando..." : "Crear desde importadas"}</span>
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="nuevo-producto-btn"
            className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors">
            <Plus size={15} /> <span className="hidden sm:inline">Nuevo producto</span><span className="sm:hidden">Nuevo</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-3 sm:p-4 flex flex-wrap gap-2 sm:gap-3 items-center">
        <div className="relative flex-1 min-w-40">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input placeholder="Buscar producto..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {["todos", ...CATEGORIES].map((c) => (
            <button key={c} onClick={() => setCatFilter(c)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                catFilter === c ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}>{c === "todos" ? "Todos" : categoryLabels[c]}</button>
          ))}
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="products-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Nombre", "Categoria", "Unidad", "Costo", "Precio", "Margen", "Stock", "Acciones"].map((h) => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">Cargando...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">Sin productos</td></tr>
            ) : filtered.map((p) => {
              const ins = isInsumo(p.category);
              const costUnit = p.cost_unit || "kg";
              const saleUnit = p.unit || "kg";
              const costDisplay = ins ? p.price : (saleUnit === "pza" ? (p.cost_per_piece || 0) : (p.total_cost_per_kg || 0));
              const priceDisplay = ins ? null : p.price;
              const margin = !ins && p.price > 0 && costDisplay > 0 ? ((p.price - costDisplay) / p.price * 100) : 0;
              const hasCosts = (p.direct_costs?.length > 0) || (p.indirect_costs?.length > 0);
              const unitLabel = ins ? p.unit : (p.purchase_unit && p.purchase_unit !== p.unit ? `${p.purchase_unit} / ${p.unit}` : p.unit);
              return (
              <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`product-row-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{p.name}</div>
                  {p.sku && <div className="text-xs text-slate-400 font-mono">{p.sku}</div>}
                  {!ins && !hasCosts && (
                    <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Sin costos</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full">
                    {categoryLabels[p.category] || p.category}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600 text-xs">{unitLabel}</td>
                <td className="px-4 py-3 text-slate-700 text-xs">
                  {costDisplay > 0 ? (
                    <span>{formatMXN(costDisplay)}<span className="text-slate-400">/{ins ? p.unit : saleUnit}</span></span>
                  ) : <span className="text-slate-300">&mdash;</span>}
                </td>
                <td className="px-4 py-3 font-medium text-slate-900">
                  {priceDisplay != null ? (
                    <span>{formatMXN(priceDisplay)}<span className="text-xs text-slate-400">/{saleUnit}</span></span>
                  ) : <span className="text-slate-300">&mdash;</span>}
                </td>
                <td className="px-4 py-3">
                  {!ins && margin > 0 ? (
                    <span className={`text-xs font-semibold ${margin > 30 ? 'text-emerald-600' : margin > 15 ? 'text-amber-600' : 'text-red-600'}`}>
                      {margin.toFixed(1)}%
                    </span>
                  ) : <span className="text-slate-300 text-xs">&mdash;</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={p.stock <= (p.min_stock || 0) && p.min_stock > 0 ? "text-red-600 font-medium text-xs" : "text-slate-700 text-xs"}>
                    {p.stock || 0} {p.unit}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    {p.estimated_total_cost_per_kg > 0 && (
                      <button onClick={() => openCostHistory(p)} className="p-1.5 hover:bg-blue-50 rounded-md transition-colors" title="Historial de costos" data-testid={`cost-history-${p.id}`}>
                        <History size={14} className="text-blue-500" />
                      </button>
                    )}
                    <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-1.5 hover:bg-slate-100 rounded-md transition-colors" data-testid={`edit-product-${p.id}`}>
                      <Edit size={14} className="text-slate-500" />
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="p-1.5 hover:bg-red-50 rounded-md transition-colors" data-testid={`delete-product-${p.id}`}>
                      <Trash2 size={14} className="text-red-400" />
                    </button>
                  </div>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="sm:hidden space-y-2">
        {loading ? (
          <div className="text-center py-12 text-slate-400">Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-slate-400">Sin productos</div>
        ) : filtered.map((p) => {
          const ins = isInsumo(p.category);
          return (
          <div key={p.id} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900 truncate">{p.name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${p.active ? "bg-brand-100 text-brand-700" : "bg-slate-100 text-slate-500"}`}>
                    {p.active ? "Activo" : "Inactivo"}
                  </span>
                </div>
                {p.sku && <div className="text-xs text-slate-400 font-mono mt-0.5">{p.sku}</div>}
                <div className="flex flex-wrap gap-x-4 mt-2 text-xs text-slate-500">
                  <span>{categoryLabels[p.category] || p.category}</span>
                  <span>{ins ? "Costo" : "Precio"}: <span className="font-semibold text-slate-800">{formatMXN(p.price)}/{p.unit}</span></span>
                  <span className={p.stock <= (p.min_stock || 0) && p.min_stock > 0 ? "text-red-600 font-medium" : ""}>
                    Stock: {p.stock || 0} {p.unit}
                  </span>
                </div>
              </div>
              <div className="flex gap-1 ml-2">
                <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                  <Edit size={15} className="text-slate-500" />
                </button>
                <button onClick={() => handleDelete(p.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors">
                  <Trash2 size={15} className="text-red-400" />
                </button>
              </div>
            </div>
          </div>
        );})}
      </div>

      {showForm && (
        <ProductForm product={editing} products={products} onClose={() => setShowForm(false)} onSave={() => { setShowForm(false); load(); }} />
      )}
      {showImport && (
        <ImportModal type="products" onClose={() => setShowImport(false)} onSuccess={() => { setShowImport(false); load(); }} />
      )}

      {/* Cost Alerts Banner */}
      {alerts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="cost-alerts-banner">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-red-600" />
            <span className="font-semibold text-red-800 text-sm">Alertas de Costos ({alerts.length})</span>
          </div>
          <div className="space-y-1.5">
            {alerts.slice(0, 5).map(a => (
              <div key={a.id} className="bg-white border border-red-100 rounded-lg px-3 py-2 flex items-center justify-between text-xs">
                <div>
                  <span className="font-medium text-slate-800">{a.product_name}</span>
                  <span className="text-slate-400 mx-1">|</span>
                  <span className="text-slate-500">Lote: {a.batch_number}</span>
                  <span className="text-slate-400 mx-1">|</span>
                  <span className="text-red-600 font-bold">Real: {formatMXN(a.real_cost)}/kg</span>
                  <span className="text-slate-400 mx-1">vs</span>
                  <span className="text-slate-600">Estimado: {formatMXN(a.estimated_cost)}/kg</span>
                  <span className="text-red-600 font-bold ml-1">({a.difference_pct > 0 ? "+" : ""}{a.difference_pct}%)</span>
                </div>
                <button onClick={async () => { await api.put(`/alerts/${a.id}/read`); loadAlerts(); }} className="text-slate-400 hover:text-slate-600 text-xs px-2">Descartar</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cost History Modal */}
      {showCostHistory && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-16 px-4" onClick={() => { setShowCostHistory(null); setCostHistory(null); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[70vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="cost-history-modal">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Historial de Costos</h2>
                <p className="text-sm text-slate-500">{showCostHistory.name}</p>
              </div>
              <button onClick={() => { setShowCostHistory(null); setCostHistory(null); }} className="text-slate-400 hover:text-slate-600 text-xl font-bold px-2">&times;</button>
            </div>
            {!costHistory ? (
              <div className="p-12 text-center text-slate-400">Cargando...</div>
            ) : (
              <div className="p-6 space-y-4">
                {costHistory.estimated_cost > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <span className="text-xs text-blue-600 font-medium">Costo estimado (meta):</span>
                      <span className="text-lg font-bold text-blue-800 ml-2">{formatMXN(costHistory.estimated_cost)}/kg</span>
                    </div>
                    <span className="text-xs text-blue-500">Umbral alerta: {costHistory.threshold_pct}%</span>
                  </div>
                )}
                {costHistory.history?.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <History size={32} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-sm">Sin historial. Completa lotes o registra compras para generar datos.</p>
                  </div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold text-slate-500">Tipo</th>
                        <th className="text-left px-3 py-2 font-semibold text-slate-500">Ref.</th>
                        <th className="text-left px-3 py-2 font-semibold text-slate-500">Fecha</th>
                        <th className="text-right px-3 py-2 font-semibold text-slate-500">Cantidad</th>
                        <th className="text-right px-3 py-2 font-semibold text-slate-500">$/kg Real</th>
                        <th className="text-right px-3 py-2 font-semibold text-slate-500">$/kg Est.</th>
                        <th className="text-right px-3 py-2 font-semibold text-slate-500">Dif.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costHistory.history.map((h, i) => (
                        <tr key={i} className={`border-b border-slate-100 ${h.alert ? "bg-red-50" : "hover:bg-slate-50"}`}>
                          <td className="px-3 py-2">
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${h.type === "lote" ? "bg-teal-100 text-teal-700" : "bg-amber-100 text-amber-700"}`}>
                              {h.type === "lote" ? "Lote" : "Compra"}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-mono text-slate-600">{h.reference}</td>
                          <td className="px-3 py-2 text-slate-500">{h.date}</td>
                          <td className="px-3 py-2 text-right text-slate-700">{h.quantity} kg</td>
                          <td className="px-3 py-2 text-right font-bold text-slate-900">{formatMXN(h.cost_per_kg)}</td>
                          <td className="px-3 py-2 text-right text-slate-500">{h.estimated_cost > 0 ? formatMXN(h.estimated_cost) : "—"}</td>
                          <td className="px-3 py-2 text-right">
                            {h.estimated_cost > 0 ? (
                              <span className={`font-bold ${h.difference > 0 ? "text-red-600" : h.difference < 0 ? "text-emerald-600" : "text-slate-400"}`}>
                                {h.difference > 0 ? "+" : ""}{h.difference_pct}%
                                {h.alert && <AlertTriangle size={10} className="inline ml-0.5 text-red-500" />}
                              </span>
                            ) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
